import { useState, useEffect } from 'react';
import { X, Plus, Minus, Flame, Leaf } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import ModifierGroup from './ModifierGroup';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export default function ItemDetailModal({ item, open, onClose }) {
  const { addToCart } = useCart();
  
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [selectedModifiers, setSelectedModifiers] = useState({});
  
  // Initialize selected modifiers state
  useEffect(() => {
    if (item && open) {
      // Reset state when modal opens
      setQuantity(1);
      setSpecialInstructions('');
      const initialModifiers = {};
      item.modifiers?.forEach(group => {
        initialModifiers[group.id] = [];
      });
      setSelectedModifiers(initialModifiers);
    }
  }, [item, open]);

  if (!item) return null;

  // Validation
  const validateModifiers = () => {
    if (!item.modifiers) return { valid: true, errors: [] };
    
    const errors = [];
    
    for (const group of item.modifiers) {
      const selected = selectedModifiers[group.id] || [];
      
      // Check required groups
      if (group.is_required && selected.length === 0) {
        errors.push(`Please select ${group.name}`);
      }
      
      // Check min selections
      if (group.min_selections > 0 && selected.length < group.min_selections) {
        errors.push(`Select at least ${group.min_selections} for ${group.name}`);
      }
      
      // Check max selections
      if (selected.length > group.max_selections) {
        errors.push(`Select up to ${group.max_selections} for ${group.name}`);
      }
    }
    
    return { valid: errors.length === 0, errors };
  };

  // Price calculation
  const calculateTotalPrice = () => {
    let total = item.price;
    
    // Add modifier prices
    Object.values(selectedModifiers).forEach(options => {
      options.forEach(option => {
        total += option.price;
      });
    });
    
    return total * quantity;
  };

  const getModifierPrice = () => {
    let modifierTotal = 0;
    Object.values(selectedModifiers).forEach(options => {
      options.forEach(option => {
        modifierTotal += option.price;
      });
    });
    return modifierTotal;
  };

  const handleModifierChange = (groupId, options) => {
    setSelectedModifiers(prev => ({
      ...prev,
      [groupId]: options
    }));
  };

  const handleAddToCart = () => {
    const validation = validateModifiers();
    
    if (!validation.valid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Build modifiers array for cart
    const cartModifiers = Object.entries(selectedModifiers)
      .filter(([_, options]) => options.length > 0)
      .map(([groupId, options]) => {
        const group = item.modifiers.find(g => g.id === parseInt(groupId));
        return {
          groupId: parseInt(groupId),
          groupName: group?.name,
          options: options.map(opt => ({
            id: opt.id,
            name: opt.name,
            price: opt.price
          }))
        };
      });

    const cartItem = {
      id: item.id,
      name: item.name,
      image_url: item.image_url,
      basePrice: item.price,
      quantity: quantity,
      modifiers: cartModifiers,
      specialInstructions: specialInstructions,
      totalPrice: calculateTotalPrice(),
      // Include dietary info for display
      is_spicy: item.is_spicy,
      is_vegetarian: item.is_vegetarian,
      is_vegan: item.is_vegan,
      is_gluten_free: item.is_gluten_free,
    };

    addToCart(cartItem);
    toast.success(`Added ${item.name} to cart`);
    onClose();
  };

  const totalPrice = calculateTotalPrice();
  const modifierPrice = getModifierPrice();
  const validation = validateModifiers();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        {/* Item Image */}
        {item.image_url && (
          <div className="h-48 md:h-64 w-full overflow-hidden rounded-t-lg">
            <img 
              src={item.image_url} 
              alt={item.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-6">
          {/* Header */}
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {item.name}
                </DialogTitle>
                
                {/* Dietary Badges */}
                {(item.is_spicy || item.is_vegetarian || item.is_vegan || item.is_gluten_free) && (
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {item.is_spicy && (
                      <Badge variant="outline" className="text-red-600 border-red-300">
                        <Flame className="h-3 w-3 mr-1" />
                        Spicy
                      </Badge>
                    )}
                    {item.is_vegetarian && (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <Leaf className="h-3 w-3 mr-1" />
                        Vegetarian
                      </Badge>
                    )}
                    {item.is_vegan && (
                      <Badge variant="outline" className="text-green-700 border-green-400">
                        Vegan
                      </Badge>
                    )}
                    {item.is_gluten_free && (
                      <Badge variant="outline">GF</Badge>
                    )}
                  </div>
                )}

                {item.description && (
                  <p className="text-gray-600 text-sm mt-2">{item.description}</p>
                )}

                {/* Price & Calories */}
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-2xl font-bold text-blue-600">
                    ${item.price.toFixed(2)}
                  </span>
                  {item.calories && (
                    <span className="text-sm text-gray-500">
                      {item.calories} cal
                    </span>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Modifiers */}
          {item.modifiers && item.modifiers.length > 0 && (
            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-lg text-gray-900">Customize Your Order</h3>
              {item.modifiers.map(group => (
                <ModifierGroup
                  key={group.id}
                  group={group}
                  selectedOptions={selectedModifiers[group.id] || []}
                  onSelectionChange={handleModifierChange}
                />
              ))}
            </div>
          )}

          {/* Special Instructions */}
          <div className="mb-6">
            <Label htmlFor="instructions" className="text-sm font-medium text-gray-700 mb-2 block">
              Special Instructions (Optional)
            </Label>
            <Textarea
              id="instructions"
              placeholder="E.g., No onions, extra sauce..."
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Quantity Selector */}
          <div className="flex items-center justify-between mb-6 pb-6 border-b">
            <span className="font-medium text-gray-900">Quantity</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="h-10 w-10"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="text-lg font-semibold w-12 text-center">
                {quantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
                disabled={quantity >= 99}
                className="h-10 w-10"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Price Breakdown */}
          {modifierPrice > 0 && (
            <div className="space-y-1 mb-4 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Base Price</span>
                <span>${item.price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Modifiers</span>
                <span>+${modifierPrice.toFixed(2)}</span>
              </div>
              {quantity > 1 && (
                <div className="flex justify-between text-gray-600">
                  <span>Quantity</span>
                  <span>× {quantity}</span>
                </div>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            disabled={!validation.valid}
            className="w-full h-14 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
          >
            Add to Cart – ${totalPrice.toFixed(2)}
          </Button>
          
          {/* Validation Error */}
          {!validation.valid && validation.errors.length > 0 && (
            <p className="text-sm text-red-600 text-center mt-2">
              {validation.errors[0]}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
